import { Reference } from "@apollo/client";
import { useCallback } from "react";

import styles from "@components/automations/AutomationCondition.module.css";
import TertiaryButton from "@components/buttons/TertiaryButton";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import AutomationConditionForm from "@components/forms/AutomationConditionForm";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useOpener from "@hooks/useOpener";
import {
  AutomationConditionDestroyDocument,
  AutomationConditionDestroyMutation,
  AutomationConditionDestroyMutationVariables,
  AutomationFragment as AutomationType
} from "@typing/Generated";

type AutomationConditionType = Extract<
  AutomationType["blocks"][number],
  { __typename: "AutomationConditionBlock" }
>["automationConditionGroups"][number]["automationConditions"][number];

type AutomationConditionGroupType = Extract<
  AutomationType["blocks"][number],
  { __typename: "AutomationConditionBlock" }
>["automationConditionGroups"][number];

type Props = {
  automationCondition: AutomationConditionType;
  automationConditionGroup: AutomationConditionGroupType;
  locked: boolean;
};

const AutomationCondition = ({ automationCondition, automationConditionGroup, locked }: Props) => {
  const deleteOpener = useOpener();

  const [destroyAutomationCondition] = useMutationWithValidation<
    AutomationConditionDestroyMutation,
    AutomationConditionDestroyMutationVariables
  >(AutomationConditionDestroyDocument, "automationCondition", {
    update: (store, { data }) => {
      if (data?.automationConditionDestroy?.id) {
        store.modify({
          fields: {
            automationConditions(cachedRefs: readonly Reference[], { readField }) {
              return cachedRefs.filter(ref => data?.automationConditionDestroy?.id !== readField("id", ref));
            }
          },
          id: store.identify({ ...automationConditionGroup })
        });
      }
    }
  });

  const handleDelete = useCallback(() => {
    destroyAutomationCondition({ id: automationCondition.id }).then(() => {
      deleteOpener.close();
    });
  }, [automationCondition.id, deleteOpener, destroyAutomationCondition]);

  return (
    <>
      <div className={styles.automationCondition}>
        <AutomationConditionForm automationCondition={automationCondition} locked={locked} />
        {!locked && (
          <div className={styles.buttons}>
            <TertiaryButton
              icon="trash"
              onClick={deleteOpener.open}
              textKey="pages.admin.automations.deleteCondition"
            />
          </div>
        )}
      </div>
      <DeleteConfirmationAlert
        messageKey="pages.admin.automations.reallyDeleteAutomationCondition"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </>
  );
};

export default AutomationCondition;
